import { Minus, Plus, AlertCircle } from "lucide-react";
import { LocationAutocomplete, type PlaceData } from "@/components/ui/location-autocomplete";
import { useBrokerCityChips, LocationChips } from "@/components/post-load/ShipmentDetailsCard";
import { cn } from "@/lib/utils";
import {
  MAX_PICKUPS,
  MAX_DROPS,
  MAX_TOTAL_STOPS,
  regenerateStops,
  pickupLabel,
  dropLabel,
  type WizardFields,
  type WizardStop,
} from "./wizardTypes";

/**
 * Guided wizard — STEP 2: ROUTE.
 *
 * "How many pickups and drops?" steppers generate exactly that many location
 * fields. Each field reuses the form's LocationAutocomplete + personalized
 * city chips (useBrokerCityChips/LocationChips from ShipmentDetailsCard) —
 * no parallel location logic. Every stop must resolve to a real city/state
 * before Next (same rule as the form's validate()).
 */

interface WizardRouteStepProps {
  fields: WizardFields;
  onChange: (patch: Partial<WizardFields>) => void;
  userId?: string;
  error?: string | null;
}

function CountStepper({
  label,
  value,
  onChange,
  max,
  disableUp,
}: {
  label: string;
  value: number;
  onChange: (v: number) => void;
  max: number;
  disableUp?: boolean;
}) {
  return (
    <div className="flex items-center justify-between rounded-xl border border-border bg-card px-4 py-3">
      <span className="text-sm font-medium text-foreground">{label}</span>
      <div className="flex items-center gap-3">
        <button
          type="button"
          aria-label={`Fewer ${label.toLowerCase()}`}
          disabled={value <= 1}
          onClick={() => onChange(value - 1)}
          className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-border text-foreground transition-colors hover:bg-muted disabled:opacity-30 disabled:hover:bg-transparent"
        >
          <Minus className="h-4 w-4" />
        </button>
        <span className="w-5 text-center text-base font-bold tabular-nums text-foreground">{value}</span>
        <button
          type="button"
          aria-label={`More ${label.toLowerCase()}`}
          disabled={value >= max || disableUp}
          onClick={() => onChange(value + 1)}
          className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-border text-foreground transition-colors hover:bg-muted disabled:opacity-30 disabled:hover:bg-transparent"
        >
          <Plus className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

function StopLocationField({
  stop,
  label,
  suggestions,
  onTextChange,
  onChipSelect,
  onChipClear,
}: {
  stop: WizardStop;
  label: string;
  suggestions: string[];
  onTextChange: (val: string, pd?: PlaceData) => void;
  onChipSelect: (label: string, pd: PlaceData) => void;
  onChipClear: () => void;
}) {
  const resolved = !!(stop.city && stop.state);
  return (
    <div>
      <label className="text-[11px] font-medium text-muted-foreground mb-1 block">
        {label} <span className="text-destructive">*</span>
      </label>
      <LocationAutocomplete
        value={stop.location}
        onChange={onTextChange}
        placeholder="City, ST or ZIP"
        className={cn(
          "h-10 text-sm",
          !resolved && stop.location ? "border-amber-500/60" : undefined
        )}
      />
      <div className="relative z-0">
        <LocationChips
          suggestions={suggestions}
          currentValue={stop.location}
          onSelect={onChipSelect}
          onClear={onChipClear}
        />
      </div>
    </div>
  );
}

export default function WizardRouteStep({ fields, onChange, userId, error }: WizardRouteStepProps) {
  const { origins: originChips, destinations: destChips } = useBrokerCityChips(userId);

  const setCounts = (pickupCount: number, dropCount: number) => {
    onChange({
      pickupCount,
      dropCount,
      stops: regenerateStops(fields.stops, pickupCount, dropCount, fields.pickupDate, fields.deliveryDate),
    });
  };

  const patchStop = (id: string, patch: Partial<WizardStop>) => {
    onChange({ stops: fields.stops.map((s) => (s.id === id ? { ...s, ...patch } : s)) });
  };

  const handleTextChange = (id: string) => (val: string, pd?: PlaceData) => {
    if (pd) {
      patchStop(id, {
        location: val,
        name: pd.name || pd.city,
        address: pd.address || `${pd.city}, ${pd.state}`,
        city: pd.city,
        state: pd.state,
        postal_code: pd.postal_code || "",
        country: pd.country || "US",
        latitude: pd.latitude ?? null,
        longitude: pd.longitude ?? null,
      });
    } else {
      // Free typing clears the previous resolution — validation requires a pick.
      patchStop(id, {
        location: val,
        name: "",
        address: "",
        city: "",
        state: "",
        latitude: null,
        longitude: null,
      });
    }
  };

  const totalStops = fields.pickupCount + fields.dropCount;
  let pickupIdx = 0;
  let dropIdx = 0;

  return (
    <div className="space-y-4">
      {/* Count pickers */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <CountStepper
          label="Pickups"
          value={fields.pickupCount}
          max={MAX_PICKUPS}
          disableUp={totalStops >= MAX_TOTAL_STOPS}
          onChange={(v) => setCounts(v, fields.dropCount)}
        />
        <CountStepper
          label="Drops"
          value={fields.dropCount}
          max={MAX_DROPS}
          disableUp={totalStops >= MAX_TOTAL_STOPS}
          onChange={(v) => setCounts(fields.pickupCount, v)}
        />
      </div>
      <p className="text-[11px] text-muted-foreground">
        Most loads are 1 pickup and 1 drop — just keep going if that's you.
      </p>

      {error && (
        <div className="flex items-start gap-2 rounded-lg border border-destructive/40 bg-destructive/5 px-3 py-2.5">
          <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5 text-destructive" />
          <p className="text-xs text-destructive">{error}</p>
        </div>
      )}

      {/* Generated location fields — pickups first, then drops */}
      <div className="space-y-4">
        {fields.stops.map((stop) => {
          const isPickup = stop.stop_type === "pickup";
          const label = isPickup
            ? pickupLabel(pickupIdx++, fields.pickupCount)
            : dropLabel(dropIdx++, fields.dropCount);
          return (
            <StopLocationField
              key={stop.id}
              stop={stop}
              label={label}
              suggestions={isPickup ? originChips : destChips}
              onTextChange={handleTextChange(stop.id)}
              onChipSelect={(lbl, pd) => handleTextChange(stop.id)(lbl, pd)}
              onChipClear={() =>
                patchStop(stop.id, {
                  location: "",
                  name: "",
                  address: "",
                  city: "",
                  state: "",
                  latitude: null,
                  longitude: null,
                })
              }
            />
          );
        })}
      </div>
    </div>
  );
}
